/*
 * Lemonade Training Oy copy rights all right reserved.
 * Product Type Definition
 */

import type { Timestamp } from "firebase/firestore";
import { VoucherProvider } from "./voucher";
import type {
  LandingMembershipLinks,
  LinkedProgrammingElementCounts,
} from "./linkedProgramming";

export const PRODUCT_TYPES = [
  "ProgrammingsProducts",
  "CoachingSubscriptionProducts",
  "MembershipProducts",
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const MEMBERSHIP_PRODUCT_TYPE: ProductType = "MembershipProducts";

export const isProductType = (value: unknown): value is ProductType =>
  typeof value === "string" &&
  (PRODUCT_TYPES as readonly string[]).includes(value);

export type ProgrammingType = "fix" | "timeline" | "reoccurring";

export type PricingType = "free" | "one time" | "subscription";

export type Price = {
  type: PricingType;
  amount: number;
};

export type ProductProgramType = "box" | "personal" | "public";
export type ProductAccessLevel = "box" | "coach" | "athlete" | "public";

// ====================================================================================================
// Membership Product Types (NEW)
// ====================================================================================================

/**
 * Type of membership product
 * - "recurring": Subscription-based memberships (monthly, 3-month, 6-month, 12-month)
 * - "visit_card": One-time purchase visit cards (1, 3, 5, 10 visits)
 */
export type MembershipType = "recurring" | "visit_card";

// ====================================================================================================
// Commitment Membership Types (F016 — commitment memberships with dual payment options)
// ====================================================================================================

/** Which payment lane a commitment pricing cell / membership is billed on. */
export type PaymentMode = "monthly" | "upfront";

/**
 * What happens when a commitment term ends (gym configuration).
 * EU consumer law forbids a third "re-lock into a new fixed term" option:
 * after a fixed term, a contract may only continue month-to-month
 * (cancellable with at most one month's notice) or expire.
 */
export type MembershipEndBehavior = "roll_monthly" | "expire";

/**
 * For roll_monthly: which monthly rate the post-commitment phase bills —
 * the gym's committed monthly rate or its no-commitment monthly rate.
 */
export type RollTargetRate = "committed" | "no_commitment";

/**
 * Length×mode key vocabulary for commitment pricing cells
 * (documentation + helper only — the PricingOptions map itself stays
 * string-indexed so grandfathered keys remain valid).
 *
 * The key is a UI-lookup convention only — term length derives from
 * `PricingOption.commitmentMonths` (see its JSDoc for the contract).
 */
export type CommitmentPricingKey = `${number}_month_${PaymentMode}`;

/** Builds a length×mode pricing key (e.g. "12_month_monthly"). */
export const commitmentPricingKey = (
  months: number,
  mode: PaymentMode,
): CommitmentPricingKey => `${months}_month_${mode}`;

/**
 * Single pricing option for membership products
 * Supports both recurring subscriptions and one-time visit cards
 */
export type PricingOption = {
  /** Stripe price ID (set by backend after Stripe price creation) */
  priceID: string;
  /** Price amount in euros */
  amount: number;
  /** Billing interval: "month" for subscriptions, "one_time" for visit cards */
  interval: "month" | "one_time";
  /** Number of months for recurring memberships (1, 3, 6, 12) */
  intervalCount?: number;
  /** Number of visits for visit cards (1, 3, 5, 10) */
  visits?: number;
  /**
   * Gym-configured validity window, in days, for a purchased card/membership.
   * Measured from the purchase. The resulting per-card expiry timestamp is
   * written to `UserBoxMembership.expiresAt` at provisioning time — there is no
   * separate expiry field on the membership for this. Omitted means no
   * time-based expiry (the card stays valid until its visits are exhausted).
   */
  validityDays?: number;
  /** Whether this pricing option is currently active/available */
  active: boolean;

  // ====================================================================================================
  // Commitment fields (F016 — all optional; ABSENCE = grandfathered old-model cell)
  // ====================================================================================================
  /** Which payment lane this cell is. Absent on old-model/visit-card cells. */
  paymentMode?: PaymentMode;
  /**
   * Commitment term in months: 3 | 6 | 12, or 24 for Finnish accounts (monthly
   * only). ABSENCE (or 1) = no commitment.
   * Consumers MUST derive term length from THIS field, never by parsing the
   * pricingOptions key string — the key is a lookup convention, not a contract.
   * An upfront commitment cell keeps `interval: "month"` + `intervalCount: N`
   * (a bounded recurring price, bounded by a Stripe subscription schedule) —
   * NOT `interval: "one_time"` — so it stays on subscription mode.
   */
  commitmentMonths?: number;
  /**
   * Upfront cells that roll to month-to-month: the connected-account monthly
   * price the schedule's release phase bills. Points at the gym's chosen
   * RollTargetRate target (a committed-rate roll needs its own provisioned
   * price even when the length is sold upfront-only).
   */
  rollMonthlyPriceID?: string;
};

/**
 * Map of all pricing options for a membership product
 * Legacy keys (still valid — grandfathered):
 *   "1_month", "3_month", "6_month", "12_month" for recurring
 *   "1_visit", "3_visit", "5_visit", "10_visit" for visit cards
 * Length×mode keys (F016 commitment cells — see CommitmentPricingKey):
 *   "{N}_month_monthly" | "{N}_month_upfront" (e.g. "12_month_upfront")
 */
export type PricingOptions = {
  [key: string]: PricingOption;
};

// ====================================================================================================
// Product Type Definition
// ====================================================================================================

export type Product = {
  id: string;
  productType: ProductType;

  // Product Type: Programming or Coaching
  programmingType?: {
    type: ProgrammingType;
  };

  active: boolean;
  name: string;
  ownerName: string;
  ownerUID: string;
  description?: string;
  pricing?: Price | null;
  priceID?: string;
  images?: string[];
  coverArtURL?: string | null;
  stripeAccountID?: string | null;
  subscribers?: string[];
  weeks?: number;
  effortLevel?: number;

  isLemonadeProduct: boolean;
  isPremium: boolean;
  createdAt: Date;
  landingPage: boolean;
  isHistoryAvailable: boolean;
  isPT: boolean;

  // will deprecate soon
  workoutUIDs: string[];
  // Deprecated
  CoachName: string;
  CoachUID: string;

  // Box relationship (NEW)
  boxID?: string | null;
  programType?: ProductProgramType;
  accessLevel?: ProductAccessLevel;

  /** PT programmes only: the coach who owned this before removeBoxStaff reassigned, kept, or ended it. */
  previousOwnerUID?: string | null;
  handedOffAt?: Timestamp | Date | null;
  /** "End it" outcome: a soft end (active: false); the programme is never deleted. */
  endedAt?: Timestamp | Date | null;
  endedBy?: string | null;

  // Product settings (NEW)
  productSettings?: {
    visibleOnLandingPage: boolean; // Store for future use
  };

  // ====================================================================================================
  // Membership Product Fields (NEW)
  // ====================================================================================================
  /**
   * Type of membership product (only for MembershipProducts)
   * - "recurring": Subscription-based memberships
   * - "visit_card": One-time purchase visit cards
   */
  membershipType?: MembershipType;

  /**
   * Multiple pricing options for membership products
   * Replaces single pricing/priceID for membership products with multiple duration/visit options
   * Example:
   * {
   *   "1_month": { priceID: "price_xxx", amount: 50, interval: "month", intervalCount: 1, active: true },
   *   "3_month": { priceID: "price_yyy", amount: 135, interval: "month", intervalCount: 3, active: true }
   * }
   */
  pricingOptions?: PricingOptions;

  /**
   * Membership only. Ordered, unique, 0..MAX_LINKED_PROGRAMMINGS, element 0 =
   * Main. Server-owned; written only by createProduct/updateProduct together
   * with linkedProgrammingUID. Read through linkedProgrammingUIDsOf.
   */
  linkedProgrammingUIDs?: string[];

  /**
   * @deprecated Mirror of linkedProgrammingUIDs[0] (null when empty); never
   * removed in F030. Read through linkedProgrammingUIDsOf.
   */
  linkedProgrammingUID?: string | null;

  /**
   * Featured flag (only for MembershipProducts with at least one linked programming)
   * Only one membership per box can be featured.
   * Featured membership determines which programming is shown prominently on the landing page.
   */
  isFeatured?: boolean;

  /**
   * Commitment-end behavior for this membership's commitment cells (gym config).
   * Absent = old-model / no commitment offered. Provisioning defaults to
   * "roll_monthly" when absent — the only legally-safe default (see
   * MembershipEndBehavior).
   */
  endBehavior?: MembershipEndBehavior;
  /**
   * When endBehavior === "roll_monthly": which monthly rate a roll lands on.
   * Drives which price rollMonthlyPriceID is provisioned to and what the
   * renewal/checkout copy promises. Ignored when endBehavior === "expire".
   */
  rollTargetRate?: RollTargetRate;

  // Note: pricing and priceID fields above are kept for backward compatibility
  // For new membership products, use pricingOptions instead

  // ====================================================================================================
  // Voucher Payment Support (NEW)
  // ====================================================================================================

  /**
   * Whether this product is eligible for sport voucher payment
   * Only applicable for MembershipProducts. Box admin can enable/disable per product.
   */
  sportVoucherEnabled?: boolean;

  /**
   * List of voucher providers accepted for this product
   * Only applicable if sportVoucherEnabled === true
   */
  acceptedVoucherProviders?: VoucherProvider[];

  /**
   * Minimum voucher amount that can be applied to this product (in cents)
   */
  minimumVoucherAmount?: number;

  /**
   * Maximum voucher amount that can be applied to this product (in cents)
   */
  maximumVoucherAmount?: number;
};

/**
 * What the unauthenticated landing-page callables (`programmings`,
 * `boxProgrammings`) return per programme. A projection, never the document:
 * subscribers, ownerUID, boxID, workoutUIDs, and CoachUID are deliberately
 * absent, so a landing response cannot leak a programme's audience.
 *
 * priceID and stripeAccountID are what the card's add-to-cart needs to build a
 * checkout; neither identifies a person.
 *
 * Built with Pick so the field names stay identical to Product and the server
 * projection is a literal key list.
 */
export type LandingProgrammeSummary = Pick<
  Product,
  | "id"
  | "name"
  | "description"
  | "coverArtURL"
  | "pricing"
  | "priceID"
  | "stripeAccountID"
  | "weeks"
  | "effortLevel"
  | "isLemonadeProduct"
> &
  // The server omits a key the stored document lacks.
  Partial<Pick<Product, "ownerName">>;

/** Response of `getLandingPageProgrammings` and `getBoxLandingPageProgrammings`. */
export type LandingProgrammingsResponse = Array<{
  id: string;
  data: LandingProgrammeSummary;
}>;

/**
 * What the unauthenticated `getBoxLandingPageMemberships` returns per
 * membership: exactly the fields the landing card and the checkout review
 * read, never the document. subscribers (the roster) and the stored link
 * fields are absent; the links arrive resolved and filtered as
 * LandingMembershipLinks.
 *
 * ownerUID, priceID, pricingOptions and stripeAccountID stay: the card puts
 * them into the cart (ownerUID as the checkout's coachUID).
 */
export type LandingMembershipSummary = Partial<
  Pick<
    Product,
    | "name"
    | "description"
    | "ownerName"
    | "ownerUID"
    | "coverArtURL"
    | "pricing"
    | "priceID"
    | "pricingOptions"
    | "membershipType"
    | "stripeAccountID"
    | "active"
    | "productType"
    | "boxID"
    | "endBehavior"
    | "rollTargetRate"
  >
> &
  LandingMembershipLinks;

/** Response of `getBoxLandingPageMemberships`; the server omits a key the stored document lacks. */
export type LandingMembershipsResponse = Array<{
  id: string;
  data: LandingMembershipSummary;
}>;

/** What a membership link change did to current holders (updateProduct response). */
export type LinkedProgrammingReconcileCounts = {
  /** Members of the box whose row for this membership was read. */
  holders: number;
  /** Holders who got the programme through this save, whichever reconcile pass (inline or trigger) wrote the row. */
  granted: number;
  repointed: number;
  /** Holders whose tagged row this save removed, whichever pass deleted it. */
  revoked: number;
  /** Holders who already had access BEFORE the save, through this membership (or own it directly). */
  unchanged: number;
  /** Holders whose own membership does not grant access (cancelled, lapsed, used-up or expired card). */
  skippedNotGranting: number;
  skippedNoUser: number;
  skippedMissingProgramming: number;
  /** Holders not granted because the linked programme is not linkable (another gym, no box, PT, or archived). */
  skippedIneligibleProgramming: number;
  /** Tagged rows kept because the holder paid for the programme directly; the membership tag is cleared instead of deleting. */
  keptDirectPurchase: number;
  pages: number;
  /** True when a newer link change overtook this run; the counts are partial and the newer run finishes. */
  superseded: boolean;
  /**
   * Per programming id over previous and next links. `holders` above is the
   * max over passes, the other totals are sums. Key order is not a contract.
   */
  perProgramming?: Record<string, LinkedProgrammingElementCounts>;
};

/**
 * updateProduct callable response. `reconcile` is null unless the request
 * carried a link field. Older callers ignore the body.
 */
export type UpdateProductResponse = {
  reconcile: LinkedProgrammingReconcileCounts | null;
};

/**
 * `details.reason` on an HttpsError a product callable throws when it refuses,
 * so clients can tell refusals that share an error code apart.
 * - created_before_payments: a membership made before the gym could take
 *   payments stays free; charging needs a new membership.
 * - payments_not_connected: creating a membership with a paid option before
 *   the gym has connected Stripe.
 * - has_commitment_members: discontinuing refused, nothing changed; members
 *   on commitment plans.
 * - commitment_members_after_off_sale: discontinue took the product off sale
 *   but ended no one's plan, because a commitment member joined meanwhile.
 * - account-mismatch: the product's Stripe account contradicts its owner or box.
 * - country_lookup_failed: (code `unavailable`) the gym's Stripe country could
 *   not be read for a term over 12 months; refused before any write, so retry.
 */
export const PRODUCT_REFUSAL_REASONS = [
  "created_before_payments",
  "payments_not_connected",
  "has_commitment_members",
  "commitment_members_after_off_sale",
  "account-mismatch",
  "country_lookup_failed",
] as const;
export type ProductRefusalReason = (typeof PRODUCT_REFUSAL_REASONS)[number];

export const isProductRefusalReason = (
  value: unknown,
): value is ProductRefusalReason =>
  typeof value === "string" &&
  (PRODUCT_REFUSAL_REASONS as readonly string[]).includes(value);

/** HttpsError `details` of a product refusal. */
export type ProductRefusalDetails = {
  reason: ProductRefusalReason;
  /**
   * has_commitment_members and commitment_members_after_off_sale: how many
   * subscriptions are on a commitment plan (the ones that blocked the call).
   */
  commitmentSubscriptionCount?: number;
};
